import { useMemo } from "react";
import type { Process } from "@archlang/scene/arch-model";
import {
  processEnds,
  buildLatencyEnv,
  type ResolvedModel,
  type ProcessModel,
  type ProcessEnd,
} from "@archlang/engine";
import { processKey } from "../processKey.js";

/**
 * The active process's ends (each `fail`/`finish` terminator + the fall-through)
 * with per-end estimates — the end-picker data (PROCESS.md §13, decision 10).
 * Empty when there's ≤1 end (no choice to offer). Pure + memoized.
 */
export function useProcessEnds(
  resolvedModel: ResolvedModel | null,
  processModels: readonly ProcessModel[],
  activeProcessId: string | null,
): readonly ProcessEnd[] {
  return useMemo(() => {
    if (!resolvedModel || !activeProcessId) return [];
    const pm = processModels.find((p) => processKey(p as unknown as Process) === activeProcessId);
    if (!pm) return [];
    try {
      const ends = processEnds(pm, buildLatencyEnv(resolvedModel, pm));
      return ends.length > 1 ? ends : []; // a single end offers no choice
    } catch {
      return [];
    }
  }, [resolvedModel, processModels, activeProcessId]);
}
